/* eslint-disable @typescript-eslint/no-use-before-define */
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'

import { classNames } from 'utils/preact/class-names'
import {
  CardElement,
  CardElementProps,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { JSX } from 'preact/jsx-runtime'
import {
  loadStripe,
  PaymentRequest,
  StripeCardElementOptions,
  StripePaymentRequestButtonElementOptions
} from '@stripe/stripe-js'
import css from '../style.module.scss'
import { formatPrice } from '../components/formatPrice'
import { Sidebar } from '../components/Sidebar'
import { Logo } from '../components/Logo'
import { OfferNote } from '../components/OfferNote'
import { $support, usePrices } from '../hooks/support'
import { $user, useUser } from '../hooks/user'
import { clear, navigate } from '../hooks/navigation'
import { t } from '../hooks/translations'

export const stripePromise = new Promise((resolve) => {
  const unsubscribe = $support.subscribe((support) => {
    if (support == null) return

    const { stripe_publishable_key: stripePublishableKey } = support
    unsubscribe()
    resolve(loadStripe(stripePublishableKey))
  })
})

export const PaymentScreen = function PaymentScreen() {
  const { offer } = useUser()
  const { currency, monthly, annually } = usePrices()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clientSecret, setClientSecret] = useState<string>(null)

  const priceId = useMemo(() => {
    if (offer === 'monthly') return monthly.price_id

    return annually.price_id
  }, [offer, monthly, annually])

  const fetchClientSecret = useCallback(async () => {
    try {
      setError(null)
      setLoading(true)

      const res = await fetch('/sign_up/stripe_payment', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ stripe_payment: { price_id: priceId } })
      })

      const data = await res.json()
      setClientSecret(data.clientSecret)
    } catch (reason) {
      setError(String(reason))
    } finally {
      setLoading(false)
    }
  }, [priceId])

  useEffect(() => {
    fetchClientSecret()
  }, [fetchClientSecret])

  return (
    <div className={css['tablet-screen']}>
      <Sidebar />
      <div>
        <div className={css['hide-for-tablet']}>
          <OfferNote />
        </div>

        {loading && (
          <div className={css['container-aligned']}>
            <p className={css.paragraph}>Loading...</p>
          </div>
        )}

        {error != null && (
          <div className={css['container-aligned']}>
            <p className={css['submit-error']}>{error}</p>
            <button
              className={classNames([css.button, css.outline, css.sm])}
              type="button"
              onClick={() => fetchClientSecret()}
            >
              {t('try_again')}
            </button>
          </div>
        )}

        {clientSecret != null && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <Logo style={{ marginBottom: 16 }} />
            <h2 className={css['payment-title']}>{t('payment_info')}</h2>
            {offer === 'yearly' && (
              <>
                <p className={css['payment-account']}>{t('account_user')}</p>
                <div className={css['payment-info']}>
                  <p className={css['payment-membership']}>{t('annual_membership')}</p>
                  <p>
                    {formatPrice({
                      prefix: currency.prefix,
                      unitAmount: annually.unit_amount / 100
                    })}
                  </p>
                </div>
                <p className={css['payment-billing']}>
                  {t('sum_billed_yearly', {
                    sum1: formatPrice({
                      prefix: currency.prefix,
                      unitAmount: annually.unit_amount / 100
                    }),
                    sum2: formatPrice({
                      prefix: currency.prefix,
                      unitAmount: annually.unit_amount / 100 / 12
                    })
                  })}
                </p>
              </>
            )}
            {offer === 'monthly' && (
              <>
                <p className={css['payment-account']}>{t('account_user')}</p>
                <div className={css['payment-info']}>
                  <p className={css['payment-membership']}>{t('monthly_membership')}</p>
                  <p>
                    {formatPrice({
                      prefix: currency.prefix,
                      unitAmount: monthly.unit_amount / 100
                    })}
                  </p>
                </div>
                <p className={css['payment-billing']}>
                  {t('billed_per_month', {
                    sum: formatPrice({
                      prefix: currency.prefix,
                      unitAmount: monthly.unit_amount / 100
                    })
                  })}
                </p>
              </>
            )}
            <StripeForm clientSecret={clientSecret} />
          </Elements>
        )}
      </div>
    </div>
  )
}

type StripeFormProps = {
  clientSecret: string
}

const StripeForm = function StripeForm({ clientSecret }: StripeFormProps) {
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)

  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState<string>(null)
  const [processing, setProcessing] = useState(false)
  const [disabled, setDisabled] = useState(true)

  const { offer } = useUser()
  const { iso, currency, monthly, annually } = usePrices()

  const activePrice = useMemo(
    () => (offer === 'monthly' ? monthly : annually),
    [offer, monthly, annually]
  )

  const stripe = useStripe()
  const elements = useElements()

  const handleLoadErrors: CardElementProps['onLoadError'] = (event) => {
    setError(String(event.error))
  }

  const handleChange: CardElementProps['onChange'] = (event) => {
    setDisabled(event.empty)
    setError(event.error ? event.error.message : null)
  }

  const handleSubmit: JSX.SubmitEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()

    if (processing) return

    try {
      setError(null)
      setProcessing(true)

      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement('card')
        }
      })

      if (payload.error != null) {
        setError(payload.error.message)
        return
      }

      setSucceeded(true)
    } catch (err) {
      setError(String(err))
    } finally {
      setProcessing(false)
    }
  }

  useEffect(() => {
    if (stripe == null) return

    let pr: PaymentRequest
    try {
      pr = stripe.paymentRequest({
        country: iso,
        currency: currency.acronym.toLowerCase(),
        total: {
          label: offer === 'monthly' ? 'Monthly subscription' : 'Annual subscription',
          amount: activePrice.unit_amount
        },
        requestPayerName: true,
        requestPayerEmail: true
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
      return
    }

    pr.canMakePayment().then((available) => {
      if (!available) return

      setPaymentRequest(pr)

      pr.on('paymentmethod', async (ev) => {
        setProcessing(true)
        setError(null)

        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        )

        if (confirmError != null) {
          // eslint-disable-next-line no-console
          console.error(confirmError)

          setError(String(confirmError))
          setProcessing(false)
          ev.complete('fail')
          return
        }

        ev.complete('success')
        if (paymentIntent.status === 'requires_action') {
          // eslint-disable-next-line @typescript-eslint/no-shadow
          const { error: confirmError } = await stripe.confirmCardPayment(clientSecret)

          if (confirmError != null) {
            // eslint-disable-next-line no-console
            console.error(confirmError)

            setError(String(confirmError))
            setProcessing(false)
            return
          }
        }

        setProcessing(false)
        setSucceeded(true)
      })
    })
  }, [stripe, iso, activePrice, currency.acronym, offer, clientSecret])

  useEffect(() => {
    if (!succeeded) return

    $user.setKey('paymentDone', true)

    navigate('Success')
    clear()
  }, [succeeded])

  const prOptions: StripePaymentRequestButtonElementOptions = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        height: '60px',
        buttonSpacing: '16px'
      }
    }
  }

  const cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        lineHeight: '1.5'
      }
    }
  }

  return (
    <form method="post" onSubmit={handleSubmit}>
      {paymentRequest != null && (
        <>
          <div className={css['payment-request']}>
            <PaymentRequestButtonElement options={prOptions} />
          </div>

          <p className={css.or}>{t('or')}</p>
        </>
      )}

      <div className={css.card}>
        <CardElement options={cardOptions} onChange={handleChange} onLoadError={handleLoadErrors} />
      </div>

      {error != null && (
        <div style={{ marginBottom: 16 }} className={css['submit-error']}>
          {error}
        </div>
      )}
      <button type="submit" className={css.button} disabled={processing || disabled || succeeded}>
        {t('continue')}
      </button>
    </form>
  )
}
